import { useState, useRef } from 'react';
import { displayAmount, centsToDollars } from '../utils/format';

interface CurrencyInputProps {
  valueCents: number;
  onChange: (cents: number) => void;
  className?: string;
}

export function CurrencyInput({ valueCents, onChange, className = '' }: CurrencyInputProps) {
  const [localValue, setLocalValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow digits, optional leading minus, optional decimal with up to 2 places
    if (raw === '' || raw === '-' || /^-?\d*\.?\d{0,2}$/.test(raw)) {
      setLocalValue(raw);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const dollarStr = valueCents === 0 ? '' : String(centsToDollars(valueCents));
    setLocalValue(dollarStr);
    setIsFocused(true);
    // Select all after state settles
    requestAnimationFrame(() => e.target.select());
  };

  const commit = () => {
    if (localValue === '' || localValue === '-') {
      onChange(0);
    } else {
      const parsed = parseFloat(localValue);
      onChange(isNaN(parsed) ? 0 : Math.round(parsed * 100));
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    commit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commit();
      inputRef.current?.blur();
    }
  };

  const displayed = displayAmount(valueCents);

  const baseClass =
    'w-full px-2 py-1 text-sm font-mono text-right border border-gray-300 dark:border-neutral-600 rounded ' +
    'focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-neutral-800 ' +
    'placeholder-gray-400 dark:placeholder-gray-600 transition-colors';

  const colorClass =
    !isFocused && valueCents < 0
      ? 'text-red-600 dark:text-red-400'
      : 'text-slate-800 dark:text-slate-100';

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={isFocused ? localValue : displayed.text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`${baseClass} ${colorClass} ${className}`}
    />
  );
}
